import { useCallback } from "react";
import { useSetAtom, useAtomValue } from "jotai";
import { Link, Popover } from "@zapier/design-system";
import {
  AssetType,
  emitEventOnChatbotsPlanUpgradeSetter,
  emitEventOnInterfacesPlanUpgradeSetter,
  formsTerminologyAtom,
} from "@zapier/canvas-state";
import useIsOwnerAdminOrSuperAdmin from "../../hooks/useIsOwnerAdminOrSuperAdmin";

type BaseProps = {
  assetType: Exclude<AssetType, undefined | "zap">;
  triggerRef: React.RefObject<HTMLElement>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

type ChatbotProps = BaseProps & {
  assetType: "chatbot";
};

type InterfaceProps = BaseProps & {
  assetType: "interface";
};

type Props = ChatbotProps | InterfaceProps;

const TITLE = "Plan Limit Reached";

export function UpsellPopover(props: Props) {
  const { assetType, triggerRef, isOpen, onOpenChange } = props;
  const isAdmin = useIsOwnerAdminOrSuperAdmin();
  const interfacesTerms = useAtomValue(formsTerminologyAtom);

  const emitEventOnChatbotsPlanUpgrade = useSetAtom(
    emitEventOnChatbotsPlanUpgradeSetter,
  );
  const emitEventOnInterfacesPlanUpgrade = useSetAtom(
    emitEventOnInterfacesPlanUpgradeSetter,
  );

  const assetTypePlural = `${assetType}s`;

  const emitEvents = useCallback(() => {
    const message = `Upgrade to create more ${assetTypePlural}.`;

    if (assetType === "chatbot") {
      emitEventOnChatbotsPlanUpgrade(message);
    } else if (assetType === "interface") {
      emitEventOnInterfacesPlanUpgrade(message);
    }
  }, [
    assetType,
    assetTypePlural,
    emitEventOnChatbotsPlanUpgrade,
    emitEventOnInterfacesPlanUpgrade,
  ]);

  return (
    <Popover
      triggerRef={triggerRef}
      placement="top left"
      offset={10}
      color="light"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      aria-label={TITLE}
      title={TITLE}
    >
      <>
        {`You've reached your plan's ${assetType === "interface" ? interfacesTerms.singularLower : assetType} limit. `}
        {isAdmin ? (
          <>
            <Link
              href={`/app/planbuilder/plans`}
              target="_blank"
              onPress={emitEvents}
            >
              Upgrade
            </Link>
            {` to create more ${assetTypePlural}.`}
          </>
        ) : (
          `Talk to your admin about upgrading to create more ${assetTypePlural}.`
        )}
      </>
    </Popover>
  );
}
